import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useEffect } from "react"
import type { TDBItem } from "@/types"

import { removeResource, resourceInitialData, type TAPIResource, updateQueries, updateResource } from "./utils"

export function useAPIResource<T extends TDBItem>(
  { fetch, create, update, remove, url }: TAPIResource<T>,
  {
    id,
    invalidate,
    mode = "initial",
    onError,
    throwOnError,
    ...props
  }: { id?: string; invalidate?: string[]; mode?: "placeholder" | "initial" } & Omit<
    UseQueryOptions<T> & Pick<UseMutationOptions, "onError">,
    "queryKey" | "queryFn"
  > = {},
  opts?,
) {
  const urlKey = url(opts)

  const queryClient = useQueryClient()
  const invalidateQueries = () =>
    (invalidate ?? [urlKey]).forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] })
    })

  const createMutation = useMutation({
    mutationFn: create,
    onSettled: invalidateQueries,
    onError: onError as any,
    throwOnError: throwOnError as any,
  })

  const updateMutation = useMutation({
    mutationFn: update,
    onSettled: invalidateQueries,
    onSuccess: (data) => updateQueries(urlKey, updateResource(data)),
    onError: onError as any,
    throwOnError: throwOnError as any,
  })

  const removeMutation = useMutation({
    mutationFn: remove,
    onSettled: invalidateQueries,
    onSuccess: (id) => updateQueries(urlKey, removeResource(id)),
    onError: onError as any,
    throwOnError: throwOnError as any,
  })

  const initialData = id ? resourceInitialData<T>(urlKey, id) : undefined

  const resourceQuery = useQuery<T>({
    throwOnError,
    enabled: !!id,
    queryKey: [urlKey, id],
    queryFn: () => fetch(id!),
    meta: { id, resource: true },
    staleTime: 600 * 1000, // 10 minutes
    // Spread the rest of the props to allow customization
    ...props,
    initialData: mode === "initial" ? initialData : undefined,
    placeholderData: mode === "placeholder" ? (initialData as any) : undefined,
  })

  useEffect(() => {
    // If the resourceQuery is enabled and has an id, we can update the queries
    if (id && resourceQuery.data) updateQueries(urlKey, updateResource(resourceQuery.data))
  }, [id, resourceQuery.data])

  return {
    ...resourceQuery,
    queryClient,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    isUpdating: updateMutation.isPending || createMutation.isPending || removeMutation.isPending,
  }
}
